import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, MapPin, Users, Shield, ChevronRight } from 'lucide-react';

/**
 * M-01: Selector de Centro de Trabajo para NOM-035
 * - Si empresa tiene 1 solo centro → bypass automático (callback inmediato)
 * - Si tiene >1 centros → muestra selector con tarjetas
 */
const NOM035CentroSelector = ({ empresaId, onSelectCentro, language = 'es' }) => {
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = {
    es: { title: 'Selecciona Centro de Trabajo', subtitle: 'Selecciona el centro de trabajo para gestionar su cumplimiento NOM-035', workers: 'trabajadores', configured: 'Configurado', pending: 'Sin configurar', matriz: 'Matriz', enter: 'Entrar' },
    en: { title: 'Select Work Center', subtitle: 'Select the work center to manage its NOM-035 compliance', workers: 'workers', configured: 'Configured', pending: 'Not configured', matriz: 'Main Office', enter: 'Enter' }
  }[language] || {};

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`/v5/nom035/${empresaId}/centros-trabajo`);
        const result = data.data;
        
        // Single center bypass
        if (result.single_center && result.centros.length === 1) {
          onSelectCentro(result.centros[0]);
          return;
        }
        
        setCentros(result.centros);
      } catch (err) {
        setError(err.response?.data?.detail || 'Error loading centers');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [empresaId, onSelectCentro]);

  if (loading) return <div className="flex items-center justify-center py-16 text-gray-400" data-testid="nom-centro-loading"><div className="animate-spin h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full mr-2" /> Loading...</div>;
  if (error) return <div className="text-center py-8 text-red-500" data-testid="nom-centro-error">{error}</div>;

  return (
    <div className="max-w-3xl mx-auto py-8" data-testid="nom-centro-selector">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-100 rounded-2xl mb-4">
          <Building2 className="h-7 w-7 text-indigo-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">{t.title}</h2>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {centros.map(centro => (
          <button
            key={centro.id}
            data-testid={`nom-centro-${centro.id}`}
            onClick={() => onSelectCentro(centro)}
            className="text-left border-2 rounded-xl p-5 hover:border-indigo-400 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">{centro.nombre}</span>
                  {centro.es_matriz && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">{t.matriz}</span>
                  )}
                </div>
                {centro.razon_social_nombre && (
                  <p className="text-xs text-gray-500 mt-1">{centro.razon_social_nombre}</p>
                )}
              </div>
              <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-indigo-500 transition-colors mt-1" />
            </div>

            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              {centro.num_trabajadores > 0 && (
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {centro.num_trabajadores} {t.workers}</span>
              )}
              {centro.razon_social_rfc && (
                <span className="flex items-center gap-1">RFC: {centro.razon_social_rfc}</span>
              )}
            </div>

            <div className="mt-3">
              {centro.tiene_config_nom035 ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                  <Shield className="h-3 w-3" /> {t.configured}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                  {t.pending}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default NOM035CentroSelector;
